import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type UniqueIdentifier,
} from '@dnd-kit/core'
import {
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  SortableContext,
} from '@dnd-kit/sortable'
import { FileMusic, Search } from 'lucide-react'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  type SVGProps,
} from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useStickyHeaderOffset } from '@/hooks/use-sticky-header-offset'
import { HelpPopover } from '@/components/ui/help-popover'
import type { Patch } from '@/data/patches'
import type { ChangedSlots } from '@/hooks/use-changed-slots'
import { makeDragAccessibility } from '@/lib/drag-accessibility'
import { formatShortcut, isApplePlatform, librarianShortcuts } from '@/lib/keyboard-shortcuts'
import { patchSlotCode } from '@/lib/patch-library'
import { resolveGridKey } from '@/lib/patch-grid-navigation'
import { cn } from '@/lib/utils'

import { droppedBank, patchDragCollision } from './bank-drop'
import {
  libraryContentClassName,
  libraryPanelHeaderClassName,
  libraryPanelTitleClassName,
  librarySearchInputClassName,
  libraryStripClassName,
} from './library-panel'
import { PatchButton } from './patch-button'

type PatchGridProps = {
  activePatchId?: string
  actions?: ReactNode
  bankLabel?: (bank: string) => string
  /** Slots whose sound has just changed, such as by Undo, which glow once. */
  changedSlots?: ChangedSlots | null
  /** Shown instead of the empty bank's import prompt when there are no patches to show. */
  emptyState?: ReactNode
  /**
   * Results from outside the workspace, shown below its slots. While there are any, the workspace
   * slots take `resultsHeading` and an empty workspace shows nothing rather than "no matches".
   */
  extraResults?: ReactNode
  /**
   * A slot to move focus to once the grid shows it, such as a patch chosen among the duplicates.
   * Each new request object asks again.
   */
  focusRequest?: { patchId: string } | null
  headerActions?: ReactNode
  isBankLoaded?: boolean
  isFavourite?: (patch: Patch) => boolean
  /** True while MIDI has no output, which a slot's title says since clicking only selects it. */
  isMidiOffline?: boolean
  isPatchDisabled?: (patch: Patch) => boolean
  onPatchChangeToFm?: (patch: Patch) => void
  onPatchCopy?: (patch: Patch) => void
  onPatchDownload?: (patch: Patch) => void
  onPatchReplace?: (patch: Patch) => void
  onPatchShare?: (patch: Patch) => void
  onPatchMove: (patch: Patch, target: Patch) => void
  /** A slot dragged onto a bank tab in the `toolbar`, other than its own bank's. */
  onPatchDropOnBank?: (patch: Patch, bank: string) => void
  onPatchEdit?: (patch: Patch) => void
  onPatchSelect?: (patch: Patch) => void
  onPatchToggleFavourite?: (patch: Patch) => void
  onImportEmptyBank?: () => void
  onLoadDemoBank?: () => void
  /** Where a patch came from, shown under its name, as Favourites shows the bank a sound left. */
  patchOrigin?: (patch: Patch) => string | undefined
  patches: Patch[]
  /** Off while the grid shows slots from several banks, which cannot be reordered together. */
  reorderable?: boolean
  resultsHeading?: string
  search: string
  searchDisabled?: boolean
  /** Tags each patch with its engine, FM or VA, while the FM1 runs Baud Girl's firmware. */
  tagsEngines?: boolean
  searchRef?: RefObject<HTMLInputElement | null>
  setSearch: (search: string) => void
  toolbar?: ReactNode
}

function PixelBankIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" viewBox="0 0 20 20" {...props}>
      <path
        d="M5 1h2v2h2V1h2v2h2V1h2v2h2v2h2v2h-2v2h2v2h-2v2h2v2h-2v2h-2v2h-2v-2H9v2H7v-2H5v-2H3v-2H1v-2h2v-2H1V9h2V7H1V5h2V3h2V1Zm0 4v10h10V5H5Zm2 2h3v3H7V7Zm4 0h2v3h-2V7Zm-4 4h3v2H7v-2Zm4 0h2v2h-2v-2Z"
        fill="currentColor"
        fillRule="evenodd"
      />
    </svg>
  )
}

/** The widths below `md`, where the bank row sticks above the list. */
const phoneQuery = '(max-width: 767.98px)'

export function PatchGrid({
  activePatchId = '',
  actions,
  bankLabel = (bank) => bank,
  changedSlots,
  emptyState,
  extraResults,
  focusRequest,
  headerActions,
  isBankLoaded = true,
  isFavourite = () => false,
  isMidiOffline = false,
  isPatchDisabled = () => false,
  onPatchChangeToFm,
  onPatchCopy,
  onPatchDownload,
  onPatchReplace,
  onPatchShare,
  onPatchMove,
  onPatchDropOnBank,
  onPatchEdit,
  onPatchSelect,
  onPatchToggleFavourite,
  onImportEmptyBank,
  onLoadDemoBank,
  patchOrigin,
  patches,
  reorderable = true,
  resultsHeading,
  search,
  searchDisabled = false,
  searchRef,
  tagsEngines = false,
  setSearch,
  toolbar,
}: PatchGridProps) {
  const { t } = useTranslation()
  const searchHint = useMemo(() => formatShortcut(librarianShortcuts.search, isApplePlatform()), [])
  const slotRefs = useRef(new Map<string, HTMLButtonElement>())
  const actionsRef = useRef<HTMLDivElement>(null)
  // On a phone the bank row sticks, so focus scrolls in below it rather than behind it.
  useStickyHeaderOffset(actionsRef, phoneQuery, 8)
  const [focusedPatchId, setFocusedPatchId] = useState('')
  const [draggedId, setDraggedId] = useState<UniqueIdentifier | null>(null)
  // The slot a menu move landed on, which takes focus once the grid shows the move. Each move is
  // a new object, so moving again asks again.
  const [movedTo, setMovedTo] = useState<{ patchId: string } | null>(null)
  // The grid is a single tab stop. It opens on the lit slot so Tab lands where
  // the user last was, and follows the arrows from there.
  const rovingPatchId = [focusedPatchId, activePatchId].find((candidate) =>
    patches.some((patch) => patch.id === candidate),
  )
  const rovingSlot = rovingPatchId ?? patches[0]?.id

  const registerSlot = (patchId: string, button: HTMLButtonElement | null) => {
    if (button) slotRefs.current.set(patchId, button)
    else slotRefs.current.delete(patchId)
  }

  // The request comes with the bank that holds the slot, so the slot is registered by the time this
  // runs.
  useEffect(() => {
    if (focusRequest) slotRefs.current.get(focusRequest.patchId)?.focus()
  }, [focusRequest])

  useEffect(() => {
    if (movedTo) slotRefs.current.get(movedTo.patchId)?.focus()
  }, [movedTo])

  // Slot ids are places, so the moved patch is now at the target's id.
  const moveSlot = (patch: Patch, offset: -1 | 1) => {
    const target = patches[patches.findIndex(({ id }) => id === patch.id) + offset]
    if (!target || target.bank !== patch.bank) return
    onPatchMove(patch, target)
    setFocusedPatchId(target.id)
    setMovedTo({ patchId: target.id })
  }

  const navigateSlots = (event: KeyboardEvent<HTMLButtonElement>, patch: Patch) => {
    // A disabled slot renders no button, so the order comes from what is there.
    const slotIds = patches.map(({ id }) => id).filter((id) => slotRefs.current.has(id))
    const next = resolveGridKey(event.key, slotIds.indexOf(patch.id), () =>
      slotIds.map((id) => slotRefs.current.get(id)?.getBoundingClientRect().top ?? 0),
    )
    if (next === null) return

    // Claimed even when the edge stops the move, so the panel does not scroll.
    event.preventDefault()
    const nextId = slotIds[next]
    if (nextId === patch.id) return

    setFocusedPatchId(nextId)
    slotRefs.current.get(nextId)?.focus()
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  // A slot is read as its code and name, and a bank tab it is dragged over as the bank. Its
  // instructions also say that a drop on a bank tab copies, which a sighted user learns only from
  // the grip's tooltip, and name Copy to… for the keyboard.
  const dragAccessibility = makeDragAccessibility(
    t,
    (id) => {
      const bank = droppedBank(id)
      if (bank !== undefined) return bankLabel(bank)
      const patch = patches.find((candidate) => candidate.id === id)
      return patch ? `${patchSlotCode(patch)} ${patch.name}` : undefined
    },
    t('ui.drag.slotInstructions'),
  )

  const finishDrag = ({ active, over }: DragEndEvent) => {
    setDraggedId(null)
    if (!over || active.id === over.id) return
    const source = patches.find((patch) => patch.id === active.id)
    const bank = droppedBank(over.id)
    if (bank !== undefined) {
      if (source && bank !== source.bank) onPatchDropOnBank?.(source, bank)
      return
    }
    const target = patches.find((patch) => patch.id === over.id)
    if (source && target && source.bank === target.bank) onPatchMove(source, target)
  }

  return (
    <Card className="synthwave-panel">
      <CardHeader className={libraryPanelHeaderClassName}>
        {/* Search covers every bank, so it sits above the bank rail rather than beside one bank. */}
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
          <CardTitle className={libraryPanelTitleClassName}>
            <PixelBankIcon aria-hidden="true" className="size-4 shrink-0" />
            {t('banks.gridTitle')}
            <HelpPopover
              className="text-[var(--crt-ink-3)] hover:text-[var(--crt-acc-lt)]"
              label={t('banks.gridTitle')}
              text={t('banks.gridDescription')}
            />
          </CardTitle>
          <label className="relative order-last block w-full sm:order-none sm:ml-auto sm:w-64">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-[var(--crt-ink-3)]" />
            <input
              aria-label={t('banks.search')}
              className={librarySearchInputClassName}
              disabled={searchDisabled}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => {
                // The page-level Escape stays out of text fields, so the
                // field clears itself where the user is most likely to press it.
                if (event.key !== 'Escape' || !search) return
                event.preventDefault()
                setSearch('')
              }}
              placeholder={t('banks.search')}
              ref={searchRef}
              title={`${t('banks.search')} (${searchHint})`}
              type="search"
              value={search}
            />
          </label>
          {/* At the row's end even when it wraps below the title, since its menu opens leftwards. */}
          {headerActions ? <div className="shrink-0 max-sm:ml-auto">{headerActions}</div> : null}
        </div>
      </CardHeader>
      {/* The bank rail shares the grid's drag context, so a slot can be dropped on a bank's tab. */}
      <DndContext
        accessibility={dragAccessibility}
        collisionDetection={patchDragCollision}
        onDragCancel={() => setDraggedId(null)}
        onDragEnd={finishDrag}
        onDragStart={({ active }) => setDraggedId(active.id)}
        sensors={sensors}
      >
        <div className="patch-area-surface flex min-w-0 items-stretch">
          {toolbar ? <div className="shrink-0">{toolbar}</div> : null}
          <div className="min-w-0 flex-1">
            <div
              className={cn(
                libraryStripClassName,
                // Below md the bank's name, menu, and Send stay in view down the whole list.
                actions && 'max-md:sticky max-md:top-0 max-md:z-30',
              )}
              ref={actionsRef}
            >
              {actions ? (
                <div className="flex w-full max-w-full min-w-0 flex-wrap items-center gap-2">
                  {actions}
                </div>
              ) : null}
            </div>
            {/*
            The hardware photo used to sit behind the grid as a half-opacity
            watermark. Against the terminal's near-black panel it washed the
            slots out rather than receding, and the masthead already carries
            the same photo, so the grid is now a plain well.
          */}
            {/* A dragged slot has to reach the bank tabs, so the grid stops clipping and rises above the rail. */}
            <CardContent
              className={cn(
                'relative isolate space-y-4',
                libraryContentClassName,
                draggedId === null ? 'overflow-hidden' : 'z-20',
              )}
              // What switches off like a CRT when its bank is deleted.
              data-patch-grid=""
            >
              <div className="relative z-10">
                {patches.length > 0 && resultsHeading ? (
                  <h3 className="font-dot-matrix mb-2 text-[13px] font-bold tracking-[0.1em] text-[var(--crt-acc-lt)] uppercase">
                    {resultsHeading}
                  </h3>
                ) : null}
                {patches.length > 0 ? (
                  <SortableContext
                    items={patches.map((patch) => patch.id)}
                    strategy={rectSortingStrategy}
                  >
                    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 xl:grid-cols-4">
                      {patches.map((patch, index) => (
                        <div className="h-full w-full" key={patch.id}>
                          <PatchButton
                            changeGlow={
                              changedSlots?.ids.has(patch.id) ? changedSlots.key : undefined
                            }
                            bankName={
                              patchOrigin?.(patch) ??
                              (reorderable ? undefined : bankLabel(patch.bank))
                            }
                            disabled={isPatchDisabled(patch)}
                            disabledTitle={t('banks.importFirst', { bank: bankLabel(patch.bank) })}
                            onChangeToFm={onPatchChangeToFm}
                            onCopy={onPatchCopy}
                            onDownload={onPatchDownload}
                            onReplace={onPatchReplace}
                            onShare={onPatchShare}
                            onEdit={onPatchEdit}
                            onNavigate={navigateSlots}
                            onSelect={onPatchSelect}
                            onToggleFavourite={onPatchToggleFavourite}
                            isFavourite={isFavourite(patch)}
                            isMidiOffline={isMidiOffline}
                            move={{
                              canMoveEarlier: patches[index - 1]?.bank === patch.bank,
                              canMoveLater: patches[index + 1]?.bank === patch.bank,
                              onMove: (offset) => moveSlot(patch, offset),
                            }}
                            patch={patch}
                            reorderable={reorderable}
                            isActive={patch.id === activePatchId}
                            registerButton={registerSlot}
                            tabIndex={patch.id === rovingSlot ? 0 : -1}
                            tagsEngine={tagsEngines}
                          />
                        </div>
                      ))}
                    </div>
                  </SortableContext>
                ) : extraResults ? null : emptyState ? (
                  emptyState
                ) : (
                  <div className="dither-well grid min-h-72 place-items-center border border-dashed border-[var(--crt-line)] p-6 text-center">
                    <div className="max-w-md border border-[var(--crt-line)] bg-[var(--crt-bg-panel3)] p-6">
                      <FileMusic className="mx-auto size-10 text-[var(--crt-acc-dim)]" />
                      <h3 className="font-dot-matrix mt-3 text-base font-bold tracking-[0.08em] text-[var(--crt-acc-lt)] uppercase">
                        {isBankLoaded ? t('banks.noMatches') : t('banks.bankEmpty')}
                      </h3>
                      {!isBankLoaded ? (
                        <>
                          <p className="mt-1 text-xs leading-6 text-[var(--crt-ink-3)]">
                            {t('banks.emptyHelp')}
                          </p>
                          <div className="mt-4 flex flex-wrap justify-center gap-2">
                            <Button onClick={onImportEmptyBank} type="button">
                              {t('banks.import')}
                            </Button>
                            <Button onClick={onLoadDemoBank} type="button" variant="secondary">
                              {t('banks.loadDemo')}
                            </Button>
                          </div>
                        </>
                      ) : null}
                    </div>
                  </div>
                )}
                {extraResults}
              </div>
            </CardContent>
          </div>
        </div>
      </DndContext>
    </Card>
  )
}
